// Pure statistics helpers (no DOM). Unit-tested in Node (test/stats.test.mjs).
// Undefined statistics (empty input) are returned as null, never NaN.

function finiteSorted(values) {
  const out = [];
  if (values == null || typeof values[Symbol.iterator] !== 'function') return out;
  let sorted = true;
  let prev = -Infinity;
  for (const v of values) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (v < prev) sorted = false;
    prev = v;
    out.push(v);
  }
  if (!sorted) out.sort((a, b) => a - b);
  return out;
}

function nearestRank(sorted, p) {
  const n = sorted.length;
  if (n === 0) return null;
  const q = Math.min(100, Math.max(0, Number(p) || 0));
  const rank = Math.min(n, Math.max(1, Math.ceil((q / 100) * n)));
  return sorted[rank - 1];
}

// Nearest-rank percentile, p in [0, 100]. Input may be unsorted and is never mutated.
// Non-finite entries are ignored. Returns null for empty input.
export function percentile(sortedOrUnsorted, p) {
  return nearestRank(finiteSorted(sortedOrUnsorted), p);
}

// { n, min, p50, p95, max, mean } — all null (n = 0) for empty input.
export function summarize(arr) {
  const s = finiteSorted(arr);
  const n = s.length;
  if (n === 0) return { n: 0, min: null, p50: null, p95: null, max: null, mean: null };
  let sum = 0;
  for (const v of s) sum += v;
  return {
    n,
    min: s[0],
    p50: nearestRank(s, 50),
    p95: nearestRank(s, 95),
    max: s[n - 1],
    mean: sum / n,
  };
}

// RFC 3550-style jitter as the mean absolute difference between successive samples
// (order preserved). Needs at least two finite samples, else null.
export function jitterRfc3550(rtts) {
  if (rtts == null || typeof rtts[Symbol.iterator] !== 'function') return null;
  let prev = null;
  let sum = 0;
  let count = 0;
  for (const v of rtts) {
    if (typeof v !== 'number' || !Number.isFinite(v)) continue;
    if (prev !== null) {
      sum += Math.abs(v - prev);
      count++;
    }
    prev = v;
  }
  return count === 0 ? null : sum / count;
}

// Loss in percent (0..100). received > sent counts as no loss; sent <= 0 gives 0.
export function lossRate(sent, received) {
  const s = Number(sent);
  const r = Number(received);
  if (!Number.isFinite(s) || s <= 0) return 0;
  const got = Number.isFinite(r) ? Math.min(Math.max(r, 0), s) : 0;
  return ((s - got) / s) * 100;
}
